import React, { Component } from 'react';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import cn from 'classnames';
import CourseAttendanceDetails from 'components/Admin/CourseAttendanceDetails';
import { apiErrorHandler } from 'core/errors';
import { API, getCancelToken, request } from 'redux/api';
import { pluralize } from 'utils';

import { ReactComponent as ChevronRightIcon } from 'assets/chevron_right.svg';

interface GetAllCoursesAttendancesResponse extends MessageResponse {
  courseData: CourseAverageAttendance[];
}

interface AllCoursesAttendanceState {
  loading: boolean;
  courses: CourseAverageAttendance[];
  courseBeingViewed: CourseAverageAttendance | null;
  showCourseAttendanceDetails: boolean;
}

type AllCoursesAttendanceProps = ToastManagerProp;

class AllCoursesAttendance extends Component<AllCoursesAttendanceProps, AllCoursesAttendanceState> {
  apiSources = getCancelToken();

  state: AllCoursesAttendanceState = {
    loading: true,
    courses: [],
    courseBeingViewed: null,
    showCourseAttendanceDetails: false,
  };

  async getAllCoursesAttendances() {
    const response = await request<GetAllCoursesAttendancesResponse>({
      url: API.ATTENDANCE_ADMIN_ALL_COURSES,
      cancelToken: this.apiSources.token,
    });
    return response.courseData;
  }

  async componentDidMount() {
    this.apiSources = getCancelToken();
    try {
      const sessions = await this.getAllCoursesAttendances();
      this.setState({ courses: sessions, loading: false });
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ loading: false });
      });
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  showAttendanceDetailsDialog = (course: CourseAverageAttendance) => {
    if (course.classesRecorded <= 0) return;
    this.setState({
      showCourseAttendanceDetails: true,
      courseBeingViewed: course,
    });
  };

  hideAttendanceDetailsDialog = () => {
    this.setState({
      showCourseAttendanceDetails: false,
    });
  };

  getRow = (course: CourseAverageAttendance) => {
    return (
      <tr
        key={course.courseId}
        className={cn({
          hovered: course.classesRecorded > 0,
          clickable: course.classesRecorded > 0,
        })}
        onClick={() => this.showAttendanceDetailsDialog(course)}
      >
        <td className='table__cell'>
          <div className='table__cell-header table__cell-header--title'>{course.courseCode}</div>
          <div className='table__cell-value table__cell-value--sm'>{course.title}</div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>COURSE ADMIN</div>
          <div className='table__cell-value'>{course.courseAdmin.name}</div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>CLASSES ON RECORD</div>
          <div className='table__cell-value'>{course.classesRecorded}</div>
        </td>
        <td className='table__cell'>
          <div className='table__cell-header'>AVG. ATTENDANCE PER CLASS</div>
          {course.averageAttendance !== undefined ? (
            <div className={cn('table__cell-value', getColorClassForPercentage(course.averageAttendance))}>
              {course.averageAttendance}%
            </div>
          ) : (
            <div className='table__cell-value'>--</div>
          )}
        </td>
        <td className={cn('table__cell', { 'table__cell--more': course.classesRecorded > 0 })}>
          {course.classesRecorded > 0 ? <ChevronRightIcon display='block' /> : null}
        </td>
      </tr>
    );
  };

  render() {
    const { loading, courses, courseBeingViewed, showCourseAttendanceDetails } = this.state;
    return (
      <div className='section'>
        <div className='section__header'>
          <h3 className='section__title'>
            {loading
              ? 'Loading attendance data...'
              : !courses.length
              ? 'Attendance not recorded in any course yet'
              : `Attendance records available for ${courses.length} ${pluralize(courses.length, 'course')}`}
          </h3>
        </div>
        <div className='section__body'>
          <table className='table table--bordered'>
            <tbody>{this.state.courses.map(this.getRow)}</tbody>
          </table>
        </div>
        <CourseAttendanceDetails
          isOpen={showCourseAttendanceDetails}
          course={courseBeingViewed}
          onClose={this.hideAttendanceDetailsDialog}
        />
      </div>
    );
  }
}

/**
 * Returns a color class name for a number between 0 and 100, returns empty string otherwise
 * @param percentage attendance percentage
 */
const getColorClassForPercentage = (percentage: number) => {
  if (percentage >= 80) return 'table__cell-value--green';
  else if (percentage >= 60) return 'table__cell-value--orange';
  return 'table__cell-value--red';
};

export default withToastManager(AllCoursesAttendance);
